import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Spinner } from "react-bootstrap";
import {
  FaBalanceScale,
  FaBarcode,
  FaBoxOpen,
  FaCheck,
  FaEdit,
  FaImage,
  FaPlus,
  FaSearch,
  FaShippingFast,
  FaSyncAlt,
  FaTags,
  FaToggleOn,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  deactivateCategory,
  getActiveCategories,
  getCategory,
  setCategoryUraMapping,
} from "../../actions/categoryActions";
import "./CategoriesTable.css";
import { usePermissions } from "../../utilities/usePermissions";
import { UraLeafPicker } from "../../components/UraReferencePicker";
import UraMappingCell from "../../components/UraMappingCell";
import { leafFromCategory } from "../../utilities/uraMapping";
import AppSelect from "../../components/ui/AppSelect";
import "../../components/catalog/catalog.css";

const PLACEHOLDER_IMAGE =
  "https://res.cloudinary.com/lusuku/image/upload/v1702635550/Lusuku_Public_Resources/No_Category_Pic_Placecholder-Prod_ltsbhf.jpg";

const Metric = ({ label, value, icon, tone }) => (
  <div className={`category-metric category-metric--${tone}`}>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
    <span className="category-metric__icon">{icon}</span>
  </div>
);

const ConfirmDeactivate = ({ category, onCancel, onConfirm }) => (
  <div
    className="category-confirm"
    role="presentation"
    onMouseDown={(event) => {
      if (event.target === event.currentTarget) onCancel();
    }}
  >
    <div
      className="category-confirm__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deactivate-category-title"
    >
      <div className="category-confirm__icon">
        <FaToggleOn />
      </div>
      <div className="category-confirm__content">
        <span className="category-confirm__eyebrow">Visibility change</span>
        <h2 id="deactivate-category-title">Deactivate category?</h2>
        <p>
          <strong>{category?.category}</strong> will move to Deactivated
          Categories and stop appearing in active category lists.
        </p>
      </div>
      <div className="category-confirm__actions">
        <button type="button" className="category-button category-button--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="category-button category-button--danger" onClick={onConfirm}>
          Deactivate
        </button>
      </div>
    </div>
  </div>
);

/**
 * How many products actually sit in this category.
 * ────────────────────────────────────────────────────────────────────────────
 * `post_listings` is a denormalised array on the category document, typed to
 * PostListing ids and written only when an admin edits the category itself. It
 * therefore misses brand, retailer/wholesaler and healthcare products entirely
 * — a brand product filed under "Packaging" left this column reading 0.
 *
 * The server now sends `productCounts` (counted live across all four
 * collections that carry a `category` ref). The `post_listings` fallback is
 * deliberate, not defensive clutter: the dashboard and the API deploy
 * separately, so until the server ships, this keeps rendering the old number
 * instead of a column of zeroes.
 */
const productCountOf = (category) => {
  const live = category?.productCounts?.total;
  if (typeof live === "number") return live;
  return category?.post_listings?.length || 0;
};

const CategoriesTable = () => {
  const { activeCategories = [], loading } = useSelector(
    (state) => state.categories
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const { can, loaded: permissionsLoaded } = usePermissions();
  const canManageCategories =
    permissionsLoaded && can("categorization:manage");
  const canCreateCategories =
    canManageCategories ||
    (permissionsLoaded && can("categorization:create"));
  const canUpdateCategories =
    canManageCategories ||
    (permissionsLoaded && can("categorization:update"));
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("group");
  const [categoryToDeactivate, setCategoryToDeactivate] = useState(null);
  /* Inline URA mapping: the row whose cell is showing the picker, and the row
     whose save is in flight. One at a time; a category is a small thing. */
  const [mappingRowId, setMappingRowId] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);

  useEffect(() => {
    dispatch(getActiveCategories());
  }, [dispatch]);

  const metrics = useMemo(() => {
    const totalListings = activeCategories.reduce(
      (sum, category) => sum + productCountOf(category),
      0
    );
    const populated = activeCategories.filter(
      (category) => productCountOf(category) > 0
    ).length;
    const uraMapped = activeCategories.filter(
      (category) => Boolean(category?.uraCommodityCode)
    ).length;

    return {
      total: activeCategories.length,
      totalListings,
      populated,
      empty: activeCategories.length - populated,
      uraMapped,
    };
  }, [activeCategories]);

  const visibleCategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = activeCategories.filter((category) => {
      if (!query) return true;
      return (
        String(category?.category || "").toLowerCase().includes(query) ||
        String(category?.groupLabel || "").toLowerCase().includes(query) ||
        (category?.aliases || []).some((alias) => String(alias).toLowerCase().includes(query)) ||
        String(category?._id || "").toLowerCase().includes(query) ||
        String(category?.uraCommodityCode || "").toLowerCase().includes(query) ||
        String(category?.uraCommodityName || "").toLowerCase().includes(query)
      );
    });

    return [...filtered].sort((a, b) => {
      const aListings = productCountOf(a);
      const bListings = productCountOf(b);
      if (sort === "listings-high") return bListings - aListings;
      if (sort === "listings-low") return aListings - bListings;
      if (sort === "ura-unmapped") {
        const byMapped = (a?.uraCommodityCode ? 1 : 0) - (b?.uraCommodityCode ? 1 : 0);
        if (byMapped !== 0) return byMapped;
      }
      if (sort === "group") {
        // The storefront rail's own order: groups, then name.
        const byGroup = (a?.groupSortOrder ?? 999) - (b?.groupSortOrder ?? 999);
        if (byGroup !== 0) return byGroup;
      }
      return String(a?.category || "").localeCompare(String(b?.category || ""));
    });
  }, [activeCategories, search, sort]);

  const editCategory = (categoryId) => {
    if (!canUpdateCategories) return;
    dispatch(getCategory(categoryId));
    history.push("/admin/category-create");
  };

  const confirmDeactivate = () => {
    if (!categoryToDeactivate?._id || !canUpdateCategories) return;
    dispatch(deactivateCategory(categoryToDeactivate._id));
    setCategoryToDeactivate(null);
  };

  /* `leaf` is the picked URA commodity leaf, or null when the (x) cleared it. */
  const saveUraMapping = async (category, leaf) => {
    if (!canUpdateCategories || !category?._id) return;
    setSavingRowId(category._id);
    const result = await dispatch(setCategoryUraMapping(category, leaf));
    setSavingRowId(null);
    if (!result?.ok) {
      toast.error(result?.message || "Could not save the URA mapping.");
      return;
    }
    setMappingRowId(null);
    toast.success(
      leaf?.code
        ? `${category.category} now maps to URA commodity code ${leaf.code}.`
        : `${category.category} no longer has a URA commodity code.`
    );
  };

  return (
    <main className="category-workspace">
      <header className="category-workspace__header">
        <div className="category-workspace__title">
          <span className="category-workspace__eyebrow">Catalog structure</span>
          <h1>
            <span><FaTags /></span>
            Active Categories
          </h1>
          <p>Organize customer discovery and monitor listing coverage.</p>
        </div>

        <div className="category-workspace__actions">
          <button
            type="button"
            className="category-button category-button--ghost"
            onClick={() => history.push("/admin/units")}
          >
            <FaBalanceScale /> Units
          </button>
          <button
            type="button"
            className="category-button category-button--ghost"
            onClick={() => history.push("/admin/shipping-cost")}
          >
            <FaShippingFast /> Shipping cost
          </button>
          <button
            type="button"
            className="category-button category-button--primary"
            onClick={() => history.push("/admin/category-create")}
            disabled={!canCreateCategories}
          >
            <FaPlus /> New category
          </button>
        </div>
      </header>

      <section className="category-metrics" aria-label="Category summary">
        <Metric label="Active categories" value={metrics.total} icon={<FaTags />} tone="blue" />
        <Metric label="Total listings" value={metrics.totalListings} icon={<FaBoxOpen />} tone="green" />
        <Metric label="With listings" value={metrics.populated} icon={<FaCheck />} tone="gold" />
        <Metric label="Empty categories" value={metrics.empty} icon={<FaImage />} tone="coral" />
        <Metric
          label="URA commodity mapped"
          value={`${metrics.uraMapped}/${metrics.total}`}
          icon={<FaBarcode />}
          tone={metrics.uraMapped === metrics.total ? "green" : "gold"}
        />
      </section>

      <section className="category-table-panel">
        <div className="category-toolbar">
          <div>
            <span className="category-toolbar__label">Category directory</span>
            <strong>
              {search
                ? `${visibleCategories.length} of ${activeCategories.length}`
                : `${activeCategories.length} active`}
            </strong>
          </div>

          <div className="category-toolbar__controls">
            <label className="category-search">
              <FaSearch />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, group, old name, ID or URA code"
                aria-label="Search active categories"
              />
            </label>
            <AppSelect
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              aria-label="Sort active categories"
            >
              <option value="group">Group, as in the shop</option>
              <option value="name">Name A–Z</option>
              <option value="listings-high">Most listings</option>
              <option value="listings-low">Fewest listings</option>
              <option value="ura-unmapped">URA unmapped first</option>
            </AppSelect>
            <button
              type="button"
              className="category-icon-button"
              onClick={() => dispatch(getActiveCategories())}
              title="Refresh categories"
              aria-label="Refresh categories"
            >
              <FaSyncAlt />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="category-state">
            <Spinner animation="border" />
            <strong>Loading categories</strong>
            <span>Refreshing category and listing data.</span>
          </div>
        ) : visibleCategories.length === 0 ? (
          <div className="category-state">
            <span className="category-state__icon"><FaSearch /></span>
            <strong>{search ? "No matching categories" : "No active categories"}</strong>
            <span>
              {search
                ? "Try another name or category ID."
                : "Create a category to begin organizing listings."}
            </span>
          </div>
        ) : (
          <div className="category-table-scroll">
            <table className="category-table">
              <thead>
                <tr>
                  <th className="category-table__number">#</th>
                  <th>Category</th>
                  <th>Group</th>
                  <th>Image</th>
                  <th>Listings</th>
                  <th>URA commodity</th>
                  <th>Status</th>
                  <th>Category ID</th>
                  <th className="category-table__actions-heading">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleCategories.map((category, index) => {
                  const listingCount = productCountOf(category);
                  return (
                    <tr key={category?._id}>
                      <td className="category-table__number">{index + 1}</td>
                      <td>
                        <div className="category-name">
                          <span><FaTags /></span>
                          <div>
                            <strong>{category?.category || "Untitled category"}</strong>
                            <small>
                              {listingCount ? "Catalog populated" : "Hidden from shoppers until it has listings"}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="lk-cat-group">{category?.groupLabel || "No group"}</span>
                      </td>
                      <td>
                        <img
                          className="category-image"
                          src={category?.category_pic || category?.displayPic || PLACEHOLDER_IMAGE}
                          alt={category?.category || "Category"}
                          title={
                            category?.category_pic
                              ? undefined
                              : "No picture uploaded. Shoppers see this group's default tile."
                          }
                        />
                      </td>
                      <td>
                        <span className={`category-listing-count ${listingCount === 0 ? "is-empty" : ""}`}>
                          {listingCount}
                        </span>
                      </td>
                      <td className="category-table__ura">
                        {mappingRowId === category?._id ? (
                          <UraLeafPicker
                            compact
                            autoFocus
                            value={leafFromCategory(category)}
                            isDisabled={savingRowId === category?._id}
                            onChange={(leaf) => saveUraMapping(category, leaf)}
                            onCancel={() => setMappingRowId(null)}
                          />
                        ) : (
                          <UraMappingCell
                            code={category?.uraCommodityCode}
                            name={category?.uraCommodityName}
                            rate={category?.uraTaxRate}
                            rowLabel={category?.category}
                            disabled={savingRowId !== null}
                            onEdit={
                              canUpdateCategories
                                ? () => setMappingRowId(category._id)
                                : null
                            }
                          />
                        )}
                      </td>
                      <td>
                        <span className="category-status">
                          <span />
                          Active
                        </span>
                      </td>
                      <td>
                        <code className="category-id" title={category?._id}>
                          {category?._id}
                        </code>
                      </td>
                      <td>
                        <div className="category-row-actions">
                          <button
                            type="button"
                            onClick={() => editCategory(category._id)}
                            disabled={!canUpdateCategories}
                            title="Edit category"
                            aria-label={`Edit ${category?.category || "category"}`}
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            className="category-row-actions__toggle"
                            onClick={() => setCategoryToDeactivate(category)}
                            disabled={!canUpdateCategories}
                            title="Deactivate category"
                            aria-label={`Deactivate ${category?.category || "category"}`}
                          >
                            <FaToggleOn />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && visibleCategories.length > 0 && (
          <footer className="category-table-footer">
            <span>
              Showing <strong>{visibleCategories.length}</strong> categor
              {visibleCategories.length === 1 ? "y" : "ies"}
            </span>
            <button type="button" onClick={() => history.push("/admin/deactive-category-table")}>
              View deactivated categories
            </button>
          </footer>
        )}
      </section>

      {categoryToDeactivate && (
        <ConfirmDeactivate
          category={categoryToDeactivate}
          onCancel={() => setCategoryToDeactivate(null)}
          onConfirm={confirmDeactivate}
        />
      )}
    </main>
  );
};

export default CategoriesTable;
